import type { DraftChunk, Lesson, LessonFile } from '../types'

const DB_NAME = 'sabak-ai-v2'
const DB_VERSION = 2
const LESSONS = 'lessons'
const FILES = 'files'
const DRAFTS = 'recording-drafts'
const PENDING = 'pending-operations'

export type PendingOperation = {
  id: string
  type: 'save-lesson' | 'upload-file'
  payload: Lesson | { lessonId: string; userId: string; file: File }
  createdAt: number
}

const openDb = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(LESSONS)) db.createObjectStore(LESSONS, { keyPath: 'id' })
      if (!db.objectStoreNames.contains(FILES)) db.createObjectStore(FILES, { keyPath: 'id' })
      if (!db.objectStoreNames.contains(DRAFTS)) {
        const store = db.createObjectStore(DRAFTS, { keyPath: 'id' })
        store.createIndex('lessonId', 'lessonId')
      }
      if (!db.objectStoreNames.contains(PENDING)) db.createObjectStore(PENDING, { keyPath: 'id' })
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

const requestToPromise = <T>(request: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

const withStore = async <T>(
  storeName: string,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
) => {
  const db = await openDb()
  return requestToPromise(action(db.transaction(storeName, mode).objectStore(storeName)))
}

export const cacheLessons = async (lessons: Lesson[]) => {
  const db = await openDb()
  const transaction = db.transaction(LESSONS, 'readwrite')
  const store = transaction.objectStore(LESSONS)
  lessons.forEach((lesson) => store.put(lesson))
  await new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

export const getCachedLessons = () =>
  withStore<Lesson[]>(LESSONS, 'readonly', (store) => store.getAll())

export const cacheLesson = (lesson: Lesson) =>
  withStore<IDBValidKey>(LESSONS, 'readwrite', (store) => store.put(lesson))

export const cacheFile = (file: LessonFile) =>
  withStore<IDBValidKey>(FILES, 'readwrite', (store) => store.put(file))

export const getCachedFiles = async (lessonId: string) => {
  const files = await withStore<LessonFile[]>(FILES, 'readonly', (store) => store.getAll())
  return files.filter((file) => file.lesson_id === lessonId)
}

export const saveDraftChunk = (chunk: DraftChunk) =>
  withStore<IDBValidKey>(DRAFTS, 'readwrite', (store) => store.put(chunk))

export const getDraftChunks = async (lessonId: string) => {
  const db = await openDb()
  const store = db.transaction(DRAFTS, 'readonly').objectStore(DRAFTS)
  const chunks = await requestToPromise(store.index('lessonId').getAll(lessonId) as IDBRequest<DraftChunk[]>)
  return chunks.sort((a, b) => a.sequence - b.sequence)
}

export const clearDraftChunks = async (lessonId: string) => {
  const chunks = await getDraftChunks(lessonId)
  const db = await openDb()
  const transaction = db.transaction(DRAFTS, 'readwrite')
  chunks.forEach((chunk) => transaction.objectStore(DRAFTS).delete(chunk.id))
  await new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

export const addPendingOperation = (operation: PendingOperation) =>
  withStore<IDBValidKey>(PENDING, 'readwrite', (store) => store.put(operation))

export const getPendingOperations = () =>
  withStore<PendingOperation[]>(PENDING, 'readonly', (store) => store.getAll())

export const removePendingOperation = (id: string) =>
  withStore<undefined>(PENDING, 'readwrite', (store) => store.delete(id))
